import React, { useEffect, useState, useMemo } from 'react';
import SelectFilter from '../../../../Components/SelectFilter';
import SelectDropdown from '../../../../Components/SelectDropdown';
import Dropdown from '../../../../Components/Dropdown';
import Btn from '../../../../Components/Btn';
import { Radio } from '../../../../Components/Checkbox/Checkbox';
import Fieldset from '../../../../Components/Fieldset';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useAiInsightsContext } from '../context/AiInsightsContext';
import {
	sourceTypes,
	insightTypes,
	areArraysEqual
} from '../utils';
import styles from '../../../Insights/Kpi.module.css';

interface DimensionValue {
  id: string;
  label: string;
  [key: string]: string | number | undefined;
}

export default function AiInsightsFilters() {
	const {
		filters,
		setFilters,
		availableDimensions,
		chartType,
		setChartType
	} = useAiInsightsContext();

	const [t] = useTranslation(['kpi', 'utils']);
	const [inputSearch, setInputSearch] = useState('');
	const [settingsIsOpen, setSettingsIsOpen] = useState(false);
	
	type Option = { label: string; value: string };
	const [list, setList] = useState<Option[]>([]);

	const resetIntention = () => setFilters({ ...filters, engagementLevel: [] });
	const handleChangeIntentionSelected = (val: string) => {
		const oldIntentions = [...filters.engagementLevel];
		const index = oldIntentions.indexOf(val);
		const newIntentions = index !== -1 ? oldIntentions.filter(x => x !== val) : [...oldIntentions, val];
		setFilters({ ...filters, engagementLevel: newIntentions });
	};

	const resetDevices = () => setFilters({ ...filters, device: [] });
	const handleChangeDeviceSelected = (val: string) => {
		const oldDevices = [...filters.device];
		const index = oldDevices.indexOf(val);
		const newDevices = index !== -1 ? oldDevices.filter(x => x !== val) : [...oldDevices, val];
		setFilters({ ...filters, device: newDevices });
	};

	const resetSource = () => setFilters({ ...filters, sourceType: [] });
	const handleChangeSourceType = (val: string) => {
		const oldSources = [...filters.sourceType];
		const index = oldSources.indexOf(val);
		const newSources = index !== -1 ? oldSources.filter(x => x !== val) : [...oldSources, val];
		setFilters({ ...filters, sourceType: newSources });
	};


	const resetInsightTypes = () => setFilters({ ...filters, insightType: [] });
	const handleChangeInsightTypeSelected = (val: string) => {
		const oldTypes = [...filters.insightType];
		const index = oldTypes.indexOf(val);
		const newTypes = index !== -1 ? oldTypes.filter(x => x !== val) : [...oldTypes, val];
		setFilters({ ...filters, insightType: newTypes });
	};

	const lastPageType = availableDimensions?.LastPageType?.values || [];
	const pageType = availableDimensions?.PageType?.values || [];
	const firstPageType = availableDimensions?.FirstPageType?.values || [];
	const pageTypeOptions = Array.from(
		new Map(
			[...lastPageType, ...pageType, ...firstPageType].map((v: DimensionValue) => [v.id, v])
		).values()
	).map((v) => ({ label: v.label, value: v.id }));

	const resetPageType = () => setFilters({ ...filters, pageType: [] });
	const handleChangePageTypeSelected = (val: string) => {
		const oldPageTypes = [...(filters.pageType || [])];
		const index = oldPageTypes.indexOf(val);
		const newPageTypes = index !== -1 ? oldPageTypes.filter(x => x !== val) : [...oldPageTypes, val];
		setFilters({ ...filters, pageType: newPageTypes });
	};

	function handleSelectedTenant(v: string) {
		if (tenantOptions.some(option => option.value === v)) {
			setFilters({ ...filters, tenant: v });
		}
	}

	const handleChangeSearch = (value: string) => {
		setInputSearch(value);
	};


	const otherOptions = useMemo(() => {
		const allowed = [
			{ key: 'VisitorType', id: 'New', label: 'Visitor type: New' },
			{ key: 'Days', id: 'Weekend', label: 'Days: Weekend' },
			{ key: 'TransactionType', id: 'PromoCode', label: 'Transaction type: Promo code' }
		];
		if (!availableDimensions) return allowed.map(opt => ({
			label: opt.label,
			value: `${opt.key}__${opt.id}`,
			key: opt.key,
			id: opt.id
		}));
		return allowed.map(opt => {
			const dim = availableDimensions[opt.key];
			const found = dim && Array.isArray(dim.values)
				? dim.values.find((v: DimensionValue) => v.id === opt.id)
				: undefined;
			return {
				label: found ? `${opt.key}: ${found.label}` : opt.label,
				value: `${opt.key}__${opt.id}`,
				key: opt.key,
				id: opt.id
			};
		});
	}, [availableDimensions]);


	const otherSelectedValues = filters.others || [];
	const handleChangeOtherSelected = (val: string) => {
		const old = [...(filters.others || [])];
		const index = old.indexOf(val);
		const newVals = index !== -1 ? old.filter(x => x !== val) : [...old, val];
		setFilters({ ...filters, others: newVals });
	};

	const resetOthers = () => setFilters({ ...filters, others: [] });
	const dropdownValue = list.some(option => option.value === filters.tenant) ? filters.tenant : null;

	const deviceDimension = availableDimensions && typeof availableDimensions === 'object' && !Array.isArray(availableDimensions)
		? availableDimensions['Device']
		: undefined;
	const deviceOptions = deviceDimension && Array.isArray(deviceDimension.values)
		? (deviceDimension.values as DimensionValue[]).map((v) => ({ label: v.label, value: v.id }))
		: [];

	// Calcul stable des tenantOptions
	const tenantOptions = useMemo(() => {
		if (
			availableDimensions &&
			typeof availableDimensions === 'object' &&
			availableDimensions.Tenant &&
			Array.isArray(availableDimensions.Tenant.values)
		) {
			return (availableDimensions.Tenant.values as DimensionValue[]).map((t) => ({
				label: t.id === '' ? 'Default' : t.label,
				value: t.id
			}))
				.concat([{ label: 'All tenants', value: 'notenant' }]);
		}
		return [];
	}, [availableDimensions]);


	const lastVisEngLevel = availableDimensions?.LastVisEngLevel?.values || [];
	const visEngLevel = availableDimensions?.VisEngLevel?.values || [];
	const firstVisEngLevel = availableDimensions?.FirstVisEngLevel?.values || [];

	const engagementLevelOptions = Array.from(
		new Map(
			[...lastVisEngLevel, ...visEngLevel, ...firstVisEngLevel].map((v: DimensionValue) => [v.id, v])
		).values()
	).map((v) => ({ label: v.label, value: v.id }));



	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		let isSameUrl = true;
		Object.entries(filters).forEach(([key, value]) => {
			const urlVal = (params.get(key) ?? '').split(',');
			if (Array.isArray(value) && !areArraysEqual(value, urlVal.filter(v => v !== ''))) {
				isSameUrl = false;
			}
			if (key === 'tenant' && value && params.get('tenant') !== value) {
				isSameUrl = false;
			}
		});
		if (!isSameUrl) {
			const newParams = new URLSearchParams(window.location.search);
			Object.entries(filters).forEach(([key, value]) => {
				if (Array.isArray(value) && value.length > 0) {
					newParams.set(key, value.join(','));
				} else if (key === 'tenant' && value) {
					newParams.set('tenant', value as string);
				} else {
					newParams.delete(key);
				}
			});
			const newUrl = window.location.pathname + (newParams.toString() ? '?' + newParams.toString() : '');
			if (newUrl !== window.location.pathname + window.location.search) {
				window.history.replaceState(null, '', newUrl);
			}
		}

		const saved = localStorage.getItem('aiInsightsFilters');
		let isSameStorage = true;
		if (saved) {
			const parsed = JSON.parse(saved);
			Object.entries(filters).forEach(([key, value]) => {
				const storageVal = parsed[key] || (key === 'tenant' ? null : []);
				if (Array.isArray(value) && !storageVal.includes(value)) {
					isSameStorage = false;
				}
				if (key === 'tenant' && value && storageVal !== value) {
					isSameStorage = false;
				}
			});
		} else {
			isSameStorage = false;
		}
		if (!isSameStorage) {
			localStorage.setItem('aiInsightsFilters', JSON.stringify(filters));
		}
	}, [filters]);

	useEffect(() => {
		if (tenantOptions.length === 0 || (tenantOptions.length === 1 && tenantOptions[0].value === 'notenant')) {
			return;
		}

		if (tenantOptions.length !== list.length || !isEqual(list, tenantOptions)) {
			setList(tenantOptions);
		}
	}, [tenantOptions]);


	return (
		<div className={styles.section_part}>
			<div className={styles.filterList}>
				<SelectFilter
					label={'Insight type'}
					onChange={handleChangeInsightTypeSelected}
					onReset={resetInsightTypes}
					value={filters.insightType}
					optionsList={insightTypes}
				/>
				
				<SelectFilter
					label={t('data.engagementLevel')}
					onChange={handleChangeIntentionSelected}
					onReset={resetIntention}
					value={filters.engagementLevel}
					optionsList={engagementLevelOptions}
				/>
				<SelectFilter
					label="Traffic source"
					onChange={handleChangeSourceType}
					onReset={resetSource}
					value={filters.sourceType}
					optionsList={sourceTypes}
				/>
				<SelectFilter
					label={t('data.device')}
					onChange={handleChangeDeviceSelected}
					onReset={resetDevices}
					value={filters.device}
					optionsList={deviceOptions}
				/>
				<SelectFilter
					label="Pagetype"
					onChange={handleChangePageTypeSelected}
					onReset={resetPageType}
					value={filters.pageType || []}
					optionsList={pageTypeOptions}
				/>
				<SelectFilter
					label="Others dimensions"
					onChange={handleChangeOtherSelected}
					onReset={resetOthers}
					value={otherSelectedValues}
					optionsList={otherOptions}
				/>
				{tenantOptions.length > 2 && 
					<SelectDropdown
						value={dropdownValue}
						optionsList={list}
						onChange={(v) => handleSelectedTenant(v)}
						color='white'
						label='Tenant'
						labelPosition='inner'
						autocomplete={true}
						onAutocomplete={(v) => handleChangeSearch(v)}
						autocompleteValue={inputSearch}
					/>
				}
				<Dropdown
					isOpen={settingsIsOpen}
					toggle={(s) => setSettingsIsOpen(s)}
					button={
						<Btn
							style="ghost"
							color="secondary"
							icon="fa fa-sliders-h"
						/>
					}
				>
					<div className='dropdown_section'>
						<Fieldset label="Chart type" hasMargin={false}>
							<Radio
								label="Line chart"
								onChange={() => setChartType('line')}
								forcedChecked={chartType === 'line'}
								fullWidth={true}
							/>
							<Radio
								label="Area chart"
								onChange={() => setChartType('area')}
								forcedChecked={chartType === 'area'}
								fullWidth={true}
							/>
							<Radio
								label="Bar chart"
								onChange={() => setChartType('bar')}
								forcedChecked={chartType === 'bar'}
								fullWidth={true}
							/>
						</Fieldset>
					</div>
				</Dropdown>
			</div>
		</div>
	);
}
